import type { Styles } from '@adornis/ass/style.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { TemporaryUser } from '@chancenwerk/shared/master-data-management/users/entities/TemporaryUser.js';
import { Status, type UserPresenceRecord } from '@chancenwerk/shared/PresenceRecord.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export function attendanceIcon(status: Status) {
  switch (status) {
    case Status.NOT_SURE:
      return 'radio_button_unchecked';
    case Status.PRESENT:
      return 'person';
    case Status.MISSING:
      return 'no_accounts';
    case Status.EXCUSED:
      return 'sensor_occupied';
    case Status.PAUSED:
      return 'pause_circle_outline';
    case Status.SCHEDULED_ABSENCE:
      return 'sensor_occupied';
    case Status.NOT_YET_REGISTERED:
      return 'person_add';
  }
}

export function attendanceColor(ctx: any, status: Status) {
  switch (status) {
    case Status.NOT_SURE:
      return ctx.colors.neutral;
    case Status.PRESENT:
      return ctx.colors.primary;
    case Status.MISSING:
      return ctx.colors.tone.error;
    case Status.EXCUSED:
      return ctx.colors.secondary;
    case Status.PAUSED:
      return '#189AB4';
    case Status.SCHEDULED_ABSENCE:
      return '#189AB4';
    case Status.NOT_YET_REGISTERED:
      return ctx.colors.tone.warning;
  }
}

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  schoolInformation: { jahrgangsstufe: 1, zug: 1 },
};

/**
 * @customElement board-component-attendance-card
 */
@customElement('board-component-attendance-card')
export class BoardComponentAttendanceCard extends ChemistryLitElement {
  @property({ attribute: false }) record?: UserPresenceRecord;
  @property({ attribute: 'empty-name' }) emptyName: string = '';

  @state() showMore: boolean = false;
  @state() grade: string = '';

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          '.name': { maxWidth: '140px', lineHeight: '1.2' },
          '.icons-right': { 'cr-icon': { cursor: 'pointer' }, 'cr-icon:hover': { opacity: '0.8' } },
          '.icon-paused': { 'cr-icon': { cursor: 'pointer' }, 'cr-icon:hover': { opacity: '0.8' } },
          '@media screen and (max-width: 490px)': {
            '.title': { width: '100%' },
            '.icons-right': {
              width: '100%',
              borderLeft: '0 !important',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              justifyContent: 'flex-end !important',
              marginLeft: '0 !important',
              paddingLeft: '0 !important',
              marginTop: this.spacing.sm,
              paddingTop: this.spacing.sm,
            },
          },
        },
      },
    ];
  }

  override connectedCallback() {
    super.connectedCallback();

    this._fetchGrade()
      .then(() => this.requestUpdate)
      .catch(console.error);
  }

  private _statusClick(status: Status) {
    if (!this.record) return;

    this.record.status = status;
    this.requestUpdate();

    const eventDis = new CustomEvent('status-click', { detail: { value: status } });
    this.dispatchEvent(eventDis);
  }

  private _commentChange(text: string) {
    if (!this.record) return;

    this.record.comment = text;
    this.requestUpdate();

    const eventDis = new CustomEvent('comment-change', { detail: { value: text } });
    this.dispatchEvent(eventDis);
  }

  private async _fetchGrade() {
    if (this.record?.user instanceof Student) {
      const stud = await Student.getByID<Student>(this.record.user._id)(studentSelector);
      this.grade = `${stud?.schoolInformation.jahrgangsstufe ?? ''}${stud?.schoolInformation.zug ?? ''}`;
    }
  }

  private _name() {
    if (this.record?.user?.vorname || this.record?.user?.nachname) {
      return html`${this.record.user.vorname} ${this.record.user.nachname}`;
    }
    return this.emptyName;
  }

  override render() {
    return html`<cr-card space="sm" padding="sm">
      <cr-flex space-between horizontal ${css({ flexWrap: 'wrap' })}>
        <cr-flex horizontal crossaxis-center space-between flex class="title">
          <cr-flex horizontal crossaxis-center>
            <cr-icon
              ${css({
                color: attendanceColor(this, this.record?.status ?? Status.NOT_SURE),
                marginRight: this.spacing.sm,
                fontSize: '32px',
              })}
            >
              ${this.record?.status === Status.EXCUSED && (this.record.excuseReason ?? '').length > 0
                ? html`<cr-tooltip>
                    <cr-flex horizontal>
                      <cr-text bold ${css({ marginRight: this.spacing.sm })}>Grund: </cr-text>
                      <cr-text>${this.record.excuseReason}</cr-text>
                    </cr-flex>
                  </cr-tooltip>`
                : ''}
              ${attendanceIcon(this.record?.status ?? Status.NOT_SURE)}
            </cr-icon>
            <cr-text class="name" ${css({ wordBreak: 'break-word' })}> ${this._name()} ${this.grade} </cr-text>
          </cr-flex>
          ${this.record?.user?.handynummer && baseConfig.get('DEPLOYMENT') === 'development'
            ? html`
                <cr-link ${css({ cursor: 'pointer' })} href="tel:${this.record.user.handynummer}">
                  <cr-icon ${css({ fontSize: '1.5rem', color: this.colors.neutral.shade(600) })}> call </cr-icon>
                </cr-link>
              `
            : nothing}
        </cr-flex>

        <cr-flex
          horizontal
          crossaxis-center
          space="sm"
          class="icons-right"
          ${css({ marginLeft: this.spacing.sm, paddingLeft: this.spacing.sm, borderLeft: '1px solid rgba(0,0,0,0.1)' })}
        >
          <cr-icon
            ${css({ color: attendanceColor(this, Status.PRESENT), fontSize: '42px' })}
            @click=${() => this._statusClick(Status.PRESENT)}
            >${attendanceIcon(Status.PRESENT)}</cr-icon
          >
          <cr-icon
            ${css({ color: attendanceColor(this, Status.EXCUSED), fontSize: '42px' })}
            @click=${() => this._statusClick(Status.EXCUSED)}
            >${attendanceIcon(Status.EXCUSED)}</cr-icon
          >
          <cr-icon
            ${css({ color: attendanceColor(this, Status.MISSING), fontSize: '42px' })}
            @click=${() => this._statusClick(Status.MISSING)}
            >${attendanceIcon(Status.MISSING)}</cr-icon
          >
          <cr-icon
            ${css({ color: this.colors.neutral, fontSize: '42px' })}
            @click=${() => (this.showMore = !this.showMore)}
          >
            more_vert
          </cr-icon>
        </cr-flex>
        ${this._more()}
      </cr-flex>
    </cr-card>`;
  }

  private _more() {
    if (!this.showMore) return nothing;

    return html`<cr-input placeholder="Kommentar" ${css({
      marginTop: this.spacing.sm,
      marginBottom: this.spacing.sm,
      width: '100%',
    })}
      .value=${this.record?.comment} 
      @value-changed=${(e: ValueEvent<string>) => this._commentChange(e.detail.value)}
    ></cr-input>

    <cr-flex horizontal space-between crossaxis-center w100 class="icon-paused" ${css({
      marginBottom: this.spacing.sm,
    })}>
        <cr-text>Pausierung</cr-text>
        <cr-icon ${css({ color: attendanceColor(this, Status.PAUSED), fontSize: '42px' })}
        @click=${() => this._statusClick(Status.PAUSED)}
        >${attendanceIcon(Status.PAUSED)}</cr-icon>
        </cr-flex>
    </cr-flex>

    <cr-flex horizontal space-between crossaxis-center w100 class="icon-paused" >
        <cr-text>Löschen</cr-text>
        <cr-icon ${css({ color: attendanceColor(this, Status.NOT_SURE), fontSize: '42px' })}
        @click=${() => this._statusClick(Status.NOT_SURE)}
        >
          ${attendanceIcon(Status.NOT_SURE)}
        </cr-icon>
      </cr-flex>

      ${this._delete()}
    </cr-flex>`;
  }

  private _delete() {
    if (!(this.record?.user instanceof TemporaryUser)) {
      return nothing;
    }

    return html`<cr-flex
      horizontal
      space-between
      crossaxis-center
      w100
      class="icon-paused"
      ${css({ marginTop: this.spacing.sm, paddingTop: this.spacing.sm, borderTop: '1px solid rgba(0,0,0,0.1)' })}
    >
      <cr-text>Benutzer Löschen</cr-text>
      <cr-icon
        ${css({ color: this.colors.tone.error, fontSize: '42px' })}
        @click=${async () => {
          if (await ChromiumDialog.confirm('Benutzer wirklich löschen?')) {
            await this.record?.user?.remove();
            this.dispatchEvent(new CustomEvent('user-deleted', { detail: { value: this.record?.userID } }));
          }
        }}
      >
        delete
      </cr-icon>
    </cr-flex>`;
  }
}
